import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal/Modal';
import Button from '../../common/Button/Button';
import BoundingBoxMap from '../../common/BoundingBoxMap/BoundingBoxMap';
import { GeocodedCity } from '../../../api/cities';
import { ApprovedWebsiteFilter, CreateSearchParams, GoogleMapsRequirements } from '../../../types/search.types';
import { Search, MapPin, Globe, Maximize2, SlidersHorizontal } from 'lucide-react';
import { formatRatingRange, ratingValueToPosition } from '../SearchForm/leadFilters';

interface ConfirmSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (radius?: number) => void;
  searchParams: CreateSearchParams;
  geocodedCity: GeocodedCity;
  isLoading?: boolean;
  websiteFilter?: ApprovedWebsiteFilter | null;
  googleMapsRequirements?: GoogleMapsRequirements;
}

const ConfirmSearchModal: React.FC<ConfirmSearchModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  searchParams,
  geocodedCity,
  isLoading = false,
  websiteFilter,
  googleMapsRequirements,
}) => {
  const [radius, setRadius] = useState<number>(0);
  const [boundingBox, setBoundingBox] = useState({
    lat_start: geocodedCity.lat_start,
    long_start: geocodedCity.long_start,
    lat_end: geocodedCity.lat_end,
    long_end: geocodedCity.long_end,
  });

  const [maxRadius, setMaxRadius] = useState<number>(50);

  useEffect(() => {
    if (isOpen) {
      setRadius(0);
      setBoundingBox({
        lat_start: geocodedCity.lat_start,
        long_start: geocodedCity.long_start,
        lat_end: geocodedCity.lat_end,
        long_end: geocodedCity.long_end,
      });

      // Calculate adaptive max radius based on city bounds
      if (geocodedCity.lat_start && geocodedCity.lat_end && geocodedCity.long_start && geocodedCity.long_end) {
        const R = 6378.1; // Earth's radius in km
        const dLat = (geocodedCity.lat_end - geocodedCity.lat_start) * Math.PI / 180;
        const dLon = (geocodedCity.long_end - geocodedCity.long_start) * Math.PI / 180;
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(geocodedCity.lat_start * Math.PI / 180) * Math.cos(geocodedCity.lat_end * Math.PI / 180) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const diagonalDistance = R * c;
        
        console.log('diagonalDistance', diagonalDistance);
        if (diagonalDistance < 5) {
          setMaxRadius(10);
        } else if (diagonalDistance < 50) {
          setMaxRadius(25);
        } else if (diagonalDistance < 60) {
          setMaxRadius(35);
        } else if (diagonalDistance < 70) {
          setMaxRadius(45);
        }
        else {
          setMaxRadius(50);
        }
      } else {
        setMaxRadius(50); // Default fallback
      }
    }
  }, [isOpen, geocodedCity]);

  const calculateBoundingBox = (lat: number, lng: number, radiusKm: number) => {
    if (radiusKm <= 0) {
      return {
        lat_start: geocodedCity.lat_start,
        long_start: geocodedCity.long_start,
        lat_end: geocodedCity.lat_end,
        long_end: geocodedCity.long_end,
      };
    }

    const R = 6378.1; // Earth's radius in km
    const dLat = radiusKm / R;
    const dLon = radiusKm / (R * Math.cos((lat * Math.PI) / 180));

    const latOffset = (dLat * 180) / Math.PI;
    const lonOffset = (dLon * 180) / Math.PI;

    return {
      lat_start: lat - latOffset,
      lat_end: lat + latOffset,
      long_start: lng - lonOffset,
      long_end: lng + lonOffset,
    };
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRadius = parseFloat(e.target.value);
    setRadius(newRadius);
    const newBbox = calculateBoundingBox(geocodedCity.lat, geocodedCity.lng, newRadius);
    setBoundingBox(newBbox);
  };

  const handleConfirm = () => {
    onConfirm(radius > 0 ? radius : undefined);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Review search area"
      size="lg"
    >
      <div className="space-y-6 py-2">
        <div className="border-b pb-4">
          <div className="flex items-center mb-2">
            <Search className="h-5 w-5 text-primary mr-2" />
            <p className="text-sm text-gray-600">Business type</p>
          </div>
          <p className="font-medium text-lg pl-7">{searchParams.search_query}</p>
        </div>

        {googleMapsRequirements && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Lead filters</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {googleMapsRequirements.has_website != null && (
                <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-800">
                  {googleMapsRequirements.has_website ? 'Has a website' : 'No website'}
                </span>
              )}
              {(googleMapsRequirements.min_review_rating != null || googleMapsRequirements.max_review_rating != null) && (
                <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-800">
                  {formatRatingRange(
                    ratingValueToPosition(googleMapsRequirements.min_review_rating ?? 0),
                    ratingValueToPosition(googleMapsRequirements.max_review_rating ?? 5),
                  )}
                </span>
              )}
            </div>
          </div>
        )}

        {websiteFilter && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Website match</p>
            <p className="mt-1 text-sm font-semibold text-blue-950">We'll prioritize businesses offering these services</p>
            {websiteFilter.offerings.length > 0 && (
              <div className="mt-3">
                <p className="mb-1 text-xs font-semibold text-blue-800">Services we'll verify on the company website</p>
                <div className="flex flex-wrap gap-1.5">
                  {websiteFilter.offerings.map(tag => <span key={tag} className="rounded-full bg-white px-2.5 py-1 text-xs text-blue-900">{tag}</span>)}
                </div>
              </div>
            )}
            <p className="mt-3 text-xs leading-relaxed text-blue-800">
              Verified website matches appear first. Broader category matches may still be used to fill the rest of your results if needed.
            </p>
          </div>
        )}

        <div className="border-b pb-4">
          <div className="flex items-start mb-4">
            <MapPin className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-medium text-lg">{geocodedCity.display_name}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <SlidersHorizontal className="h-5 w-5 text-primary mr-2" />
                <label htmlFor="radius-slider" className="text-sm font-medium text-gray-700">
                  Search Radius
                </label>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {radius === 0 ? 'City Limits' : `${radius} km`}
              </span>
            </div>

            <div className="relative pt-1">
              <input
                id="radius-slider"
                type="range"
                min="0"
                max={maxRadius}
                step="1"
                value={radius}
                onChange={handleRadiusChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                <span>City Limits</span>
                <span>{Math.round(maxRadius / 2)} km</span>
                <span>{maxRadius} km</span>
              </div>
            </div>

            {radius > 0 && (
              <div className="mt-3 bg-amber-50 p-3 rounded-md text-xs text-amber-700 flex items-start border border-amber-200">
                <svg className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p>Larger radius means more ground to cover, so your search may take a bit longer.</p>
              </div>
            )}
          </div>

          {/* Map showing the search area */}
          <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
            {isOpen && (
              <BoundingBoxMap
                key={`modal-map-${isOpen ? 'open' : 'closed'}`}
                lat={geocodedCity.lat}
                lng={geocodedCity.lng}
                lat_start={boundingBox.lat_start}
                long_start={boundingBox.long_start}
                lat_end={boundingBox.lat_end}
                long_end={boundingBox.long_end}
                radius={radius}
                display_name={geocodedCity.display_name}
                height="300px"
                forceRefresh={isOpen}
                disableWarning={true}
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 border-b pb-4">
          <div className="flex items-center">
            <Globe className="h-5 w-5 text-primary mr-2" />
            <div>
              <p className="text-sm text-gray-600">Latitude</p>
              <p className="font-medium">{geocodedCity.lat.toFixed(6)}</p>
            </div>
          </div>
          <div className="flex items-center">
            <Maximize2 className="h-5 w-5 text-primary mr-2" />
            <div>
              <p className="text-sm text-gray-600">Longitude</p>
              <p className="font-medium">{geocodedCity.lng.toFixed(6)}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-md text-sm text-blue-700 flex items-start">
          <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>Check the highlighted area on the map. If it looks right, start the search. Use the radius slider to widen the area if needed.</p>
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          onClick={handleConfirm}
          isLoading={isLoading}
        >
          Start search
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmSearchModal;
